/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */


import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { $SimpleChanges } from '@ng-falcon/+legacy/+model/on-changes/simple-changes';
import { NgfFormControl } from '@ng-falcon/ngf-form/+model/ngf-form-control';
import { noop, Subscription } from 'rxjs';
import { distinctUntilChanged, skip, tap } from 'rxjs/operators';

let nextUniqueId = 0;

@Component({
    selector: 'ngf-autocomplete-input',
    templateUrl: './autocomplete-input.component.html',
    styleUrls: ['./autocomplete-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AutocompleteInputComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  placeholder: string;
  @Input()
  hasError = false;
  @Input()
  hasShadow = false;
  @Input()
  displayValue: string | null;
  @Input()
  isOpen: boolean;
  @Input()
  disabled = false;
  @Input()
  disableClear: boolean;
  @Input()
  showOverflowTooltip = false;
  @Input()
  isRequired = false;
  @Input()
  ariaLabelledby?: string;
  @Input()
  ariaDescribedby?: string;
  @Input()
  clearHint?: string;

  readonly clearHintId = `ngf-autocomplete-clear-hint-${nextUniqueId++}`;

  @Output()
  search = new EventEmitter<string>();
  @Output()
  toggleOptions = new EventEmitter<boolean>();
  @Output()
  clearValue = new EventEmitter();
  @Output()
  focusLost = new EventEmitter();

  @ViewChild('inputRef')
  inputRef: ElementRef<HTMLInputElement>;

  constructor(@Inject(DOCUMENT) private document: Document,
              private elementRef: ElementRef<HTMLElement>) {}

  private sub: Subscription;

  searchControl = new NgfFormControl('');

  ngOnInit(): void {
    this.sub = this.handleSearchChange$().subscribe(noop);
  }

  ngOnChanges(changes: $SimpleChanges<this>): void {
    if (changes.disabled) {
      this.disabled ? this.searchControl.disable() : this.searchControl.enable();
      this.clearSearch();
    }

    if (changes.displayValue) {
      const { previousValue, currentValue, firstChange } = changes.displayValue;
      if (currentValue == null) {
        this.clearSearch();
      }

      // Preserve focus only when the *ngIf branch actually swaps (truthy ↔ falsy).
      // Skip first-change and falsy↔falsy noise from async-pipe init, which would
      // otherwise focus a stale ViewChild and steal the user's caret.
      const branchSwapped = !firstChange && (!!previousValue !== !!currentValue);
      if (branchSwapped && this.elementRef.nativeElement.contains(this.document.activeElement)) {
        this.document.defaultView.setTimeout(() => this.inputRef?.nativeElement?.focus());
      }
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** describedby for the readonly input shown while a value is selected: the consumer's ids plus the clear hint. */
  get selectedDescribedBy(): string | null {
    const ids = [this.ariaDescribedby, this.clearHint ? this.clearHintId : null].filter(Boolean);
    return ids.length ? ids.join(' ') : null;
  }

  handleToggle() {
    this.toggleOptions.emit(!this.isOpen);
  }

  handleClear() {
    if (this.displayValue) {
      this.clearValue.emit();
    }

    this.clearSearch();
  }

  handleBlur() {
    this.focusLost.emit();
  }

  private clearSearch() {
    this.searchControl.setValue('');
  }

  private handleSearchChange$() {
    return this.searchControl.value$.pipe(
      distinctUntilChanged(),
      skip(1),
      tap(value => this.search.emit(value))
    );
  }

}
